<script>
    import { selectedItem, currentUser, 
             analyzeStadgarLoading, analyzeStadgarLoadingDone, 
             createStadgarExpertLoading, createStadgarExpertLoadingDone,
             stadgarUploadLoading, stadgarUploadLoadingDone // For stadgar upload step
            } from '../store/store.js';
    import { getFileName, fetchBrf } from '../utils/utils.js';
	import { handleCreateBrfExpert } from '../utils/analyze.js'; // analyzeUploadedStadgar is now called by UploadFile
    import Logotype from './Logotype.svelte';
    import UploadFile from './UploadFile.svelte'; 
    import Login from './common/Login.svelte';
    import { get } from 'svelte/store';

    // Event handlers for the UploadFile component
    function handleStadgarUploadProgress(event) {
        const { stage, loading, done, error, type } = event.detail;
        if (type !== 'stadgar') return;

        if (stage === 'upload') {
            stadgarUploadLoading.set(loading);
            stadgarUploadLoadingDone.set(done && !error);
        } else if (stage === 'analysis') {
            analyzeStadgarLoading.set(loading);
            analyzeStadgarLoadingDone.set(done && !error);
        } else if (stage === 'expert') {
            // createStadgarExpertLoading/Done are set by handleCreateBrfExpert in utils/analyze.js
            // This event primarily signals initiation from UploadFile's perspective.
            if (loading) createStadgarExpertLoading.set(true);
            // We rely on the global store for the actual 'done' state of expert creation.
        }
        if (error) {
            console.error(`Error during ${stage} for stadgar:`, event.detail.message);
            // Potentially reset specific loading states here if needed, though UploadFile tries to manage its UI.
        }
    }

    function handleStadgarUploadComplete(event) {
        const { fileName, coreId, type } = event.detail;
        if (type !== 'stadgar') return;

        console.log(`Stadgar upload and initial processing complete for ${fileName}, coreId: ${coreId}`);
        // $selectedItem is updated by fetchBrf called within UploadFile.
        // UI will reactively switch from UploadFile to the analysis/viewer section.
    }

    function handleStadgarUploadError(event) {
        const { message, type } = event.detail;
        if (type !== 'stadgar') return;
        console.error('Stadgar Upload Error from UploadFile component:', message);
        // Reset relevant global stores to ensure UI consistency
        stadgarUploadLoading.set(false);
        stadgarUploadLoadingDone.set(false);
        analyzeStadgarLoading.set(false);
        analyzeStadgarLoadingDone.set(false);
    }

    // This function is still used if stadgar_file_name exists but stadgar_id is null
     async function handleTriggerExistingStadgarAnalysis() {
        if (!$selectedItem?.stadgar_file_name || !$selectedItem?.core_id) return;

        const coreId = $selectedItem.core_id;
        const stadgarFileName = $selectedItem.stadgar_file_name;
        const storeState = get(currentUser);

        // Set initial loading states
        analyzeStadgarLoading.set(true);
        analyzeStadgarLoadingDone.set(false);
        createStadgarExpertLoading.set(true); 
        createStadgarExpertLoadingDone.set(false);

        // 1. Start the long background expert creation process.
        // We don't await this here, as we want to proceed with analysis.
        // handleCreateBrfExpert manages its own loading/done states and error handling.
        handleCreateBrfExpert(stadgarFileName, coreId, 'stadgar')
            .then(expertResult => {
                console.log('Background expert creation process completed or initiated:', expertResult);
                // createStadgarExpertLoadingDone is set by handleCreateBrfExpert
            })
            .catch(expertError => {
                console.error('Error in background expert creation process:', expertError.message || expertError);
                // createStadgarExpertLoadingDone is set to false by handleCreateBrfExpert on error
            });

        // 2. Start and await the quick stadgar analysis.
        try {
            const analyzeUrl = 'https://brf-analyze-backend-tnkskvn76a-lz.a.run.app/analyze_stadgar_structured_initialized';
            const analyzeQueryParams = new URLSearchParams({ stadgar_file_name: stadgarFileName });
            if (coreId) {
                analyzeQueryParams.append('core_id', coreId);
            }
            const response = await fetch(`${analyzeUrl}?${analyzeQueryParams.toString()}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${storeState?.accessToken}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to trigger backend function for existing stadgar. Status:', response.status, 'Body:', errorText);
                throw new Error('Failed to trigger backend function for existing stadgar');
            }
            
            const analyzeResultData = await response.json();
            console.log('Stadgar analysis triggered, response data:', analyzeResultData);
            
            analyzeStadgarLoadingDone.set(true); // Mark analysis as done

            // 3. Fetch BRF data immediately after successful analysis.
            fetchBrf(coreId); 

        } catch (error) {
            console.error('Error in re-analyzing stadgar (analysis step):', error.message || error);
            analyzeStadgarLoadingDone.set(false);
        } finally {
            analyzeStadgarLoading.set(false); // Analysis part is finished (success or fail)
        }
	}

</script>

{#if !$currentUser?.accessToken && !$selectedItem.stadgar_file_name}
<div class="key-value-table solo-table" style="max-width: 310px; margin-bottom: 40px;">
  <h3>Boanalys har inte tillgång till stadgarna</h3>
  <p>Du behöver logga in för att ladda upp stadgarna.</p>
</div>
<Login />
{:else if !$selectedItem.stadgar_file_name}
  <!-- Section for uploading stadgar if not available -->
  <UploadFile
    uploadType="stadgar"
    associatedCoreId={$selectedItem.core_id}
    textsConfig={{ 
        title: `Ladda upp Stadgar för ${$selectedItem.name || 'denna förening'}`,
        buttonText: 'Välj Stadgar (PDF)',
        infoText: 'Ladda upp stadgarna för att möjliggöra analys och frågor till BoBot.'
    }}
    on:progress={handleStadgarUploadProgress}
    on:complete={handleStadgarUploadComplete}
    on:error={handleStadgarUploadError}
  />

{:else if $selectedItem.stadgar_file_name}
  <!-- Stadgar file exists. stadgar_id might be null or populated. -->

  <!-- Section for initial analysis state (if file exists but stadgar_id is null) -->
  {#if $selectedItem.stadgar_id === null}
    <div class="analyze-about-text" style="margin-top: 0px !important;">
      {#if !$analyzeStadgarLoading && !$createStadgarExpertLoading} 
        <!-- Show button to start analysis if no relevant process is active for this initial step -->
        <div class="ready-state centered-layout">
            <div class="content-container">
                <h5>Analysera stadgarna för djupare insikter!</h5>
                <p class="description">
                    Stadgarna finns tillgängliga men är inte analyserade än. Starta analysen för att:
                </p>
                <ul class="benefits-list">
                    <li>Förstå föreningens regler och struktur.</li>
                    <li>Identifiera eventuella avvikelser från standard.</li>
                    <li>Möjliggöra frågor till BoBot om stadgarna.</li>
                </ul>
				<p></p>
                <button
                    type="button"
                    class="action-button"
                    on:click={handleTriggerExistingStadgarAnalysis}
                >
                    Starta Analys <span class="button-arrow">&rarr;</span>
                </button>
            </div>
        </div>
      {:else if $analyzeStadgarLoading} <!-- Only show analysis loading if it's active for this initial step -->
        <!-- Box 1: Analysis Loading -->
        <div class="loading-container">
            <h3 class="loading-header">Analyserar stadgar...</h3>
            <div class="loading-items-wrapper">
                <div class="loading-item">
                  <div class="question">
                    <Logotype scale={0.6}></Logotype> 
                  </div>
                  <div class="loading-text"> 
                    <span>Analyserar stadgarna (ca 40 sek)...</span>
                  </div>
              </div>
            </div>
        </div>
      {/if} 
      <!-- The expert creation loading box (Box 2) will be handled by the general section below -->
    </div>
  {/if} <!-- End of initial analysis state block ($selectedItem.stadgar_id === null) -->

  <!-- PDF Viewer (always show if file exists, stadgar_id is not null, and user has access) -->
  {#if ($currentUser?.subscription_tier === 'Pro' || $currentUser?.subscription_tier === 'Plus') && $selectedItem.stadgar_id !== null}
    <div class="pdf-container" style="margin-top: 20px;">
      <iframe class="iframe" title="stadgar" src={ getFileName($selectedItem?.stadgar_file_name) } frameborder=0 ></iframe>
    </div>
  {/if}

  <!-- Box 2: General Expert Creation Loading Card -->
  <!-- Shows if expert creation is loading AND we are in a view where a stadgar_file_name exists (i.e., not the UploadFile view) -->
  {#if $createStadgarExpertLoading && $selectedItem.stadgar_file_name}
  <div class="analyze-about-text" style={$analyzeStadgarLoading && $selectedItem.stadgar_id === null ? 'margin-top: 15px;' : 'margin-top: 20px;'}>
    <div class="loading-container expert-loading-container">
      <h3 class="loading-header">
        {#if $analyzeStadgarLoading && $selectedItem.stadgar_id === null}
            Parallell process: Skapar Expert
        {:else}
            Bakgrundsprocess: Skapar Expert
        {/if}
      </h3>
      <div class="loading-items-wrapper">
        <div class="loading-item">
            <div class="question">
				<Logotype scale={0.6}></Logotype> 
			</div>
            <div class="loading-text"> <span>Skapar expert (detta kan ta upp till 5 min)...</span> </div>
        </div>
      </div>
    </div>
  </div>
  {/if}
{/if} 

<style>
    p.description {
        font-size: 0.95rem;
        color: #495057;
        line-height: 1.6;
        margin-bottom: 15px;
        text-align: center;
    }
    .expert-loading-container {
        border-color: #ced4da; 
    }
</style>